<template>
  <div class="login-container">
    <canvas id="canvas-bg"></canvas>
    <div class="split-container">
      <div class="left-intro-area">
        <h1 class="hero-title">
          欢迎回到<br>
          <span class="highlight">小葱聊天室</span>
        </h1>
        <p class="hero-desc">
          为了保证聊天的质量和高效性。请输入您的身份信息以接入加密网络。
        </p>
      </div>
      <div class="right-login-area">
        <div class="login-card">
          <div class="card-header">
            <h2>小葱聊天室</h2>
            <p>快来和我聊天吧！</p>
          </div>
          
          <div class="login-tabs">
            <div :class="['tab-item', { active: loginType === 'password' }]" @click="loginType = 'password'">账号密码</div>
            <div :class="['tab-item', { active: loginType === 'email' }]" @click="loginType = 'email'">邮箱验证码</div>
            <div :class="['tab-item', { active: loginType === 'phone' }]" @click="loginType = 'phone'">手机验证码</div>
          </div>

          <div class="form-container">
            <transition name="form-fade" mode="out-in">
              <!-- 账号密码登录 -->
              <div v-if="loginType === 'password'" key="password" class="form">
                <div class="form-group">
                  <input v-model="form.account" class="custom-input" placeholder="请输入邮箱或手机号" @keyup.enter="handleLogin" />
                </div>
                <div class="form-group">
                  <input v-model="form.password" type="password" class="custom-input" placeholder="请输入密码" @keyup.enter="handleLogin" />
                </div>
                <div class="form-group captcha-group">
                  <input v-model="form.captchaInput" class="custom-input captcha-input" placeholder="验证码" @keyup.enter="handleLogin" />
                  <div class="captcha-img-box" @click="generateCaptcha">{{ captcha }}</div>
                </div>
                <button @click="handleLogin" class="login-btn">登 录</button>
              </div>

              <!-- 邮箱验证码登录 -->
              <div v-else-if="loginType === 'email'" key="email" class="form">
                <div class="form-group">
                  <input 
                    v-model="form.email" 
                    class="custom-input"
                    :class="{ 'error-input': emailError }"
                    placeholder="邮箱" 
                    @blur="validateEmail"
                    @input="clearEmailError"
                    @keyup.enter="handleEmailLogin"
                  />
                  <div v-if="emailError" class="error-message">{{ emailError }}</div>
                </div>
                <div class="form-group code-group">
                  <input v-model="form.emailCode" class="custom-input code-input" placeholder="验证码" @keyup.enter="handleEmailLogin" />
                  <button @click="sendEmailCode" :disabled="emailCountdown > 0 || !!emailError" class="btn-code">
                    {{ emailCountdown > 0 ? `${emailCountdown}秒` : '发送验证码' }}
                  </button>
                </div>
                <button @click="handleEmailLogin" class="login-btn">登 录</button>
              </div>

              <!-- 手机验证码登录 -->
              <div v-else-if="loginType === 'phone'" key="phone" class="form">
                <div class="form-group">
                  <input 
                    v-model="form.phone" 
                    class="custom-input"
                    :class="{ 'error-input': phoneError }"
                    placeholder="手机号" 
                    @blur="validatePhone"
                    @input="clearPhoneError"
                    @keyup.enter="handlePhoneLogin"
                  />
                  <div v-if="phoneError" class="error-message">{{ phoneError }}</div>
                </div>
                <div class="form-group code-group">
                  <input v-model="form.phoneCode" class="custom-input code-input" placeholder="验证码" @keyup.enter="handlePhoneLogin" />
                  <button @click="sendSmsCode" :disabled="phoneCountdown > 0 || !!phoneError" class="btn-code">
                    {{ phoneCountdown > 0 ? `${phoneCountdown}秒` : '发送验证码' }}
                  </button>
                </div>
                <button @click="handlePhoneLogin" class="login-btn">登 录</button>
              </div>
            </transition>
          </div>

          <div class="card-footer">
            <span @click="$router.push('/register')" class="footer-link footer-link-left">
              <span class="link-highlight">还没有账号？去注册</span>
            </span>
            <span @click="showForgotPassword = true" class="footer-link footer-link-right">忘记密码？</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 找回密码弹窗 -->
    <transition name="modal-fade">
      <div v-if="showForgotPassword" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>找回密码</h3>
          <button class="close-btn" @click="showForgotPassword = false">×</button>
        </div>
        <div class="modal-tabs">
          <div :class="['tab-item', { active: forgotType === 'email' }]" @click="switchForgotType('email')">邮箱找回</div>
          <div :class="['tab-item', { active: forgotType === 'phone' }]" @click="switchForgotType('phone')">手机找回</div>
        </div>
        <transition name="form-fade" mode="out-in">
          <div class="modal-form" :key="forgotType">
            <div class="form-group">
              <input 
                v-if="forgotType === 'email'" 
                v-model="forgotForm.email" 
                class="custom-input"
                :class="{ 'error-input': forgotEmailError }"
                placeholder="邮箱" 
                @blur="validateForgotEmail"
                @input="clearForgotEmailError"
              />
              <input 
                v-if="forgotType === 'phone'" 
                v-model="forgotForm.phone" 
                class="custom-input"
                :class="{ 'error-input': forgotPhoneError }"
                placeholder="手机号" 
                @blur="validateForgotPhone"
                @input="clearForgotPhoneError"
              />
              <div v-if="forgotType === 'email' && forgotEmailError" class="error-message">{{ forgotEmailError }}</div>
              <div v-if="forgotType === 'phone' && forgotPhoneError" class="error-message">{{ forgotPhoneError }}</div>
            </div>
            <div class="form-group code-group">
              <input v-model="forgotForm.code" class="custom-input code-input" placeholder="验证码" />
              <button 
                @click="sendForgotCode" 
                :disabled="forgotCountdown > 0 || !!forgotEmailError || !!forgotPhoneError" 
                class="btn-code"
              >
                {{ forgotCountdown > 0 ? `${forgotCountdown}秒` : '发送验证码' }}
              </button>
            </div>
            <div class="form-group">
              <input 
                v-model="forgotForm.newPassword" 
                type="password" 
                class="custom-input"
                :class="{ 'error-input': forgotPasswordError && forgotForm.newPassword.length < 8 }"
                placeholder="新密码（至少8位）" 
                @input="handleForgotPasswordInput"
                @blur="validateForgotNewPassword"
              />
              <div v-if="forgotPasswordError && forgotForm.newPassword.length < 8" class="error-message">{{ forgotPasswordError }}</div>
              <!-- 密码强度指示器 -->
              <div v-if="forgotForm.newPassword && forgotPasswordStrengthLevel > 0" class="password-strength">
                <div class="strength-label">密码强度：</div>
                <div class="strength-bars">
                  <div class="strength-bar" :class="'strength-' + forgotPasswordStrengthLevel"></div>
                </div>
                <div class="strength-text">{{ getForgotStrengthText(forgotPasswordStrengthLevel) }}</div>
              </div>
            </div>
            <div class="form-group">
              <input 
                v-model="forgotForm.confirmPassword" 
                type="password" 
                class="custom-input"
                :class="{ 'error-input': forgotConfirmPasswordError }"
                placeholder="确认密码" 
                @input="handleForgotConfirmPasswordInput"
                @blur="validateForgotConfirmPassword"
              />
              <div v-if="forgotConfirmPasswordError" class="error-message">{{ forgotConfirmPasswordError }}</div>
            </div>
            <button @click="handleForgotPassword" class="login-btn">重置密码</button>
          </div>
        </transition>
      </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { login, emailLogin, phoneLogin, sendEmailCode, sendSmsCode, forgotPassword, generateCaptcha, checkEmail, checkPhone } from '@/api/auth'

export default {
  name: 'Login',
  data() {
    return {
      loginType: 'password',
      form: {
        account: '',
        password: '',
        captchaInput: '',
        email: '',
        emailCode: '',
        phone: '',
        phoneCode: ''
      },
      captcha: '',
      emailCountdown: 0,
      phoneCountdown: 0,
      showForgotPassword: false,
      forgotType: 'email',
      forgotForm: {
        email: '',
        phone: '',
        code: '',
        newPassword: '',
        confirmPassword: ''
      },
      forgotCountdown: 0,
      // 错误提示
      emailError: '',
      phoneError: '',
      forgotEmailError: '',
      forgotPhoneError: '',
      forgotPasswordError: '',
      forgotConfirmPasswordError: '',
      forgotPasswordStrengthLevel: 0
    }
  },
  mounted() {
    this.generateCaptcha()
    // 使用 nextTick 确保 DOM 完全渲染后再初始化 Canvas
    this.$nextTick(() => {
      // 延迟一点时间确保路由过渡动画完成
      setTimeout(() => {
        this.initCanvas()
      }, 100)
    })
  },
  methods: {
    async generateCaptcha() {
      try {
        const res = await generateCaptcha()
        this.captcha = res.captcha
      } catch (e) {
        this.captcha = Math.random().toString(36).substring(2, 6).toUpperCase()
      }
    },
    async handleLogin() {
      if (!this.form.account || !this.form.password || !this.form.captchaInput) {
        this.$message.warning('请填写完整信息')
        return
      }
      try {
        const res = await login({
          account: this.form.account,
          password: this.form.password,
          captcha: this.captcha,
          captchaInput: this.form.captchaInput
        })
        localStorage.setItem('token', res.token)
        // 确保 userInfo 包含 id 字段
        const userInfo = {
          id: res.userId || res.id,
          userId: res.userId || res.id,
          username: res.username,
          nickname: res.nickname,
          email: res.email,
          phone: res.phone,
          avatar: res.avatar,
          admin: res.admin
        }
        localStorage.setItem('userInfo', JSON.stringify(userInfo))
        this.$message.success('登录成功')
        this.$router.push('/chat')
      } catch (e) {
        this.$message.error(e.message || '登录失败')
        this.generateCaptcha()
      }
    },
    async handleEmailLogin() {
      if (!this.form.email || !this.form.emailCode) {
        this.$message.warning('请填写完整信息')
        return
      }
      try {
        const res = await emailLogin({
          email: this.form.email,
          code: this.form.emailCode
        })
        localStorage.setItem('token', res.token)
        const userInfo = {
          id: res.userId || res.id,
          userId: res.userId || res.id,
          username: res.username,
          nickname: res.nickname,
          email: res.email,
          phone: res.phone,
          avatar: res.avatar,
          admin: res.admin
        }
        localStorage.setItem('userInfo', JSON.stringify(userInfo))
        this.$message.success('登录成功')
        this.$router.push('/chat')
      } catch (e) {
        this.$message.error(e.message || '登录失败')
      }
    },
    async handlePhoneLogin() {
      if (!this.form.phone || !this.form.phoneCode) {
        this.$message.warning('请填写完整信息')
        return
      }
      try {
        const res = await phoneLogin({
          phone: this.form.phone,
          code: this.form.phoneCode
        })
        localStorage.setItem('token', res.token)
        const userInfo = {
          id: res.userId || res.id,
          userId: res.userId || res.id,
          username: res.username,
          nickname: res.nickname,
          email: res.email,
          phone: res.phone,
          avatar: res.avatar,
          admin: res.admin
        }
        localStorage.setItem('userInfo', JSON.stringify(userInfo))
        this.$message.success('登录成功')
        this.$router.push('/chat')
      } catch (e) {
        this.$message.error(e.message || '登录失败')
      }
    },
    // 验证邮箱格式
    validateEmailFormat(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },
    // 验证手机号格式
    validatePhoneFormat(phone) {
      const phoneRegex = /^1[3-9]\d{9}$/
      return phoneRegex.test(phone)
    },
    // 验证邮箱（格式和存在性）
    async validateEmail() {
      this.emailError = ''
      if (!this.form.email) {
        return
      }
      // 先验证格式
      if (!this.validateEmailFormat(this.form.email)) {
        this.emailError = '邮箱格式不正确'
        return false
      }
      // 再检查账号是否存在
      try {
        const res = await checkEmail(this.form.email)
        if (res && res.code === 0) {
          if (!res.exists) {
            this.emailError = '该账号不存在'
            return false
          }
        }
      } catch (e) {
        this.emailError = '验证失败，请稍后重试'
        return false
      }
      return true
    },
    // 验证手机号（格式和存在性）
    async validatePhone() {
      this.phoneError = ''
      if (!this.form.phone) {
        return
      }
      // 先验证格式
      if (!this.validatePhoneFormat(this.form.phone)) {
        this.phoneError = '手机号格式不正确'
        return false
      }
      // 再检查账号是否存在
      try {
        const res = await checkPhone(this.form.phone)
        if (res && res.code === 0) {
          if (!res.exists) {
            this.phoneError = '该账号不存在'
            return false
          }
        }
      } catch (e) {
        this.phoneError = '验证失败，请稍后重试'
        return false
      }
      return true
    },
    // 清除邮箱错误
    clearEmailError() {
      this.emailError = ''
    },
    // 清除手机号错误
    clearPhoneError() {
      this.phoneError = ''
    },
    // 验证找回密码的邮箱（格式和存在性）
    async validateForgotEmail() {
      this.forgotEmailError = ''
      if (!this.forgotForm.email) {
        return
      }
      // 先验证格式
      if (!this.validateEmailFormat(this.forgotForm.email)) {
        this.forgotEmailError = '邮箱格式不正确'
        return false
      }
      // 再检查账号是否存在
      try {
        const res = await checkEmail(this.forgotForm.email)
        if (res && res.code === 0) {
          if (!res.exists) {
            this.forgotEmailError = '该账号不存在'
            return false
          }
        }
      } catch (e) {
        this.forgotEmailError = '验证失败，请稍后重试'
        return false
      }
      return true
    },
    // 验证找回密码的手机号（格式和存在性）
    async validateForgotPhone() {
      this.forgotPhoneError = ''
      if (!this.forgotForm.phone) {
        return
      }
      // 先验证格式
      if (!this.validatePhoneFormat(this.forgotForm.phone)) {
        this.forgotPhoneError = '手机号格式不正确'
        return false
      }
      // 再检查账号是否存在
      try {
        const res = await checkPhone(this.forgotForm.phone)
        if (res && res.code === 0) {
          if (!res.exists) {
            this.forgotPhoneError = '该账号不存在'
            return false
          }
        }
      } catch (e) {
        this.forgotPhoneError = '验证失败，请稍后重试'
        return false
      }
      return true
    },
    // 清除找回密码的邮箱错误
    clearForgotEmailError() {
      this.forgotEmailError = ''
    },
    // 清除找回密码的手机号错误
    clearForgotPhoneError() {
      this.forgotPhoneError = ''
    },
    // 切换找回密码类型
    switchForgotType(type) {
      // 切换类型时清除所有错误提示
      this.forgotEmailError = ''
      this.forgotPhoneError = ''
      this.forgotPasswordError = ''
      this.forgotConfirmPasswordError = ''
      this.forgotPasswordStrengthLevel = 0
      this.forgotType = type
    },
    // 检查密码强度
    checkForgotPasswordStrength(pwd) {
      if (!pwd) return 0
      let score = 0
      if (pwd.length >= 8) score++
      if (/[A-Z]/.test(pwd)) score++
      if (/[a-z]/.test(pwd)) score++
      if (/\d/.test(pwd)) score++
      if (/[^A-Za-z0-9]/.test(pwd)) score++
      if (score >= 4) return 3
      if (score === 3) return 2
      return score > 0 ? 1 : 0
    },
    getForgotStrengthText(level) {
      if (level === 3) return '强'
      if (level === 2) return '中'
      if (level === 1) return '弱'
      return ''
    },
    handleForgotPasswordInput() {
      const pwd = this.forgotForm.newPassword || ''
      if (!pwd) {
        this.forgotPasswordStrengthLevel = 0
        this.forgotPasswordError = ''
        return
      }
      this.forgotPasswordStrengthLevel = this.checkForgotPasswordStrength(pwd)
      if (pwd.length < 8) {
        this.forgotPasswordError = '密码长度至少8位'
      } else if (this.forgotForm.confirmPassword && pwd !== this.forgotForm.confirmPassword) {
        this.forgotPasswordError = ''
        this.forgotConfirmPasswordError = '两次输入的密码不一致'
      } else {
        this.forgotPasswordError = ''
        if (this.forgotForm.confirmPassword && pwd === this.forgotForm.confirmPassword) {
          this.forgotConfirmPasswordError = ''
        }
      }
    },
    validateForgotNewPassword() {
      const pwd = this.forgotForm.newPassword || ''
      if (!pwd) {
        this.forgotPasswordError = '请输入新密码'
        this.forgotPasswordStrengthLevel = 0
        return false
      }
      if (pwd.length < 8) {
        this.forgotPasswordError = '密码长度至少8位'
        this.forgotPasswordStrengthLevel = this.checkForgotPasswordStrength(pwd)
        return false
      }
      this.forgotPasswordError = ''
      this.forgotPasswordStrengthLevel = this.checkForgotPasswordStrength(pwd)
      return true
    },
    handleForgotConfirmPasswordInput() {
      if (this.forgotForm.newPassword && this.forgotForm.confirmPassword) {
        if (this.forgotForm.newPassword !== this.forgotForm.confirmPassword) {
          this.forgotConfirmPasswordError = '两次输入的密码不一致'
        } else {
          this.forgotConfirmPasswordError = ''
        }
      }
    },
    validateForgotConfirmPassword() {
      if (!this.forgotForm.confirmPassword) {
        if (this.forgotForm.newPassword) {
          this.forgotConfirmPasswordError = '请再次输入密码'
        }
        return false
      }
      if (this.forgotForm.newPassword !== this.forgotForm.confirmPassword) {
        this.forgotConfirmPasswordError = '两次输入的密码不一致'
        return false
      }
      this.forgotConfirmPasswordError = ''
      return true
    },
    async sendEmailCode() {
      // 先验证格式和账号存在性
      const isValid = await this.validateEmail()
      if (!isValid) {
        return
      }
      try {
        await sendEmailCode({ email: this.form.email, scene: 'login' })
        this.startCountdown('email')
        this.$message.success('验证码已发送')
      } catch (e) {
        this.$message.error(e.message || '发送失败')
      }
    },
    async sendSmsCode() {
      // 先验证格式和账号存在性
      const isValid = await this.validatePhone()
      if (!isValid) {
        return
      }
      try {
        await sendSmsCode({ phone: this.form.phone, scene: 'login' })
        this.startCountdown('phone')
        this.$message.success('验证码已发送')
      } catch (e) {
        this.$message.error(e.message || '发送失败')
      }
    },
    async sendForgotCode() {
      // 先验证格式和账号存在性
      let isValid = false
      if (this.forgotType === 'email') {
        isValid = await this.validateForgotEmail()
        if (!isValid) {
          return
        }
      } else {
        isValid = await this.validateForgotPhone()
        if (!isValid) {
          return
        }
      }
      
      const contact = this.forgotType === 'email' ? this.forgotForm.email : this.forgotForm.phone
      if (!contact) {
        this.$message.warning(`请先输入${this.forgotType === 'email' ? '邮箱' : '手机号'}`)
        return
      }
      try {
        if (this.forgotType === 'email') {
          await sendEmailCode({ email: contact, scene: 'reset_password' })
        } else {
          await sendSmsCode({ phone: contact, scene: 'reset_password' })
        }
        this.startCountdown('forgot')
        this.$message.success('验证码已发送')
      } catch (e) {
        this.$message.error(e.message || '发送失败')
      }
    },
    async handleForgotPassword() {
      if (!this.forgotForm.code || !this.forgotForm.newPassword || !this.forgotForm.confirmPassword) {
        this.$message.warning('请填写完整信息')
        return
      }
      if (!this.validateForgotNewPassword() || !this.validateForgotConfirmPassword()) {
        return
      }
      if (this.forgotForm.newPassword.length < 8) {
        this.$message.warning('密码长度至少8位')
        return
      }
      if (this.forgotForm.newPassword !== this.forgotForm.confirmPassword) {
        this.$message.warning('两次输入的密码不一致')
        return
      }
      try {
        await forgotPassword({
          method: this.forgotType,
          contact: this.forgotType === 'email' ? this.forgotForm.email : this.forgotForm.phone,
          code: this.forgotForm.code,
          newPassword: this.forgotForm.newPassword,
          confirmPassword: this.forgotForm.confirmPassword
        })
        this.$message.success('密码重置成功，请登录')
        this.showForgotPassword = false
        // 清除表单和错误状态
        this.forgotForm = {
          email: '',
          phone: '',
          code: '',
          newPassword: '',
          confirmPassword: ''
        }
        this.forgotEmailError = ''
        this.forgotPhoneError = ''
        this.forgotPasswordError = ''
        this.forgotConfirmPasswordError = ''
        this.forgotPasswordStrengthLevel = 0
      } catch (e) {
        this.$message.error(e.message || '重置失败')
      }
    },
    startCountdown(type) {
      if (type === 'email') {
        this.emailCountdown = 60
        const timer = setInterval(() => {
          this.emailCountdown--
          if (this.emailCountdown <= 0) {
            clearInterval(timer)
          }
        }, 1000)
      } else if (type === 'phone') {
        this.phoneCountdown = 60
        const timer = setInterval(() => {
          this.phoneCountdown--
          if (this.phoneCountdown <= 0) {
            clearInterval(timer)
          }
        }, 1000)
      } else if (type === 'forgot') {
        this.forgotCountdown = 60
        const timer = setInterval(() => {
          this.forgotCountdown--
          if (this.forgotCountdown <= 0) {
            clearInterval(timer)
          }
        }, 1000)
      }
    },
    initCanvas() {
      // 如果已经初始化过，先清理
      if (this._canvasCleanup) {
        this._canvasCleanup()
        this._canvasCleanup = null
        this._canvasAnimationId = null
      }
      
      const canvas = document.getElementById('canvas-bg')
      if (!canvas) {
        // 如果 Canvas 元素不存在，延迟重试（最多重试5次）
        if (!this._canvasRetryCount) {
          this._canvasRetryCount = 0
        }
        if (this._canvasRetryCount < 5) {
          this._canvasRetryCount++
          console.warn(`Canvas 元素未找到，100ms 后重试 (${this._canvasRetryCount}/5)...`)
          setTimeout(() => {
            this.initCanvas()
          }, 100)
        } else {
          console.error('Canvas 元素初始化失败，已达到最大重试次数')
          this._canvasRetryCount = 0
        }
        return
      }
      
      // 重置重试计数
      this._canvasRetryCount = 0
      
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        console.error('无法获取 Canvas 2D 上下文')
        return
      }
      
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight

      let particlesArray
      const numberOfParticles = 70

      const mouse = { x: null, y: null, radius: 150 }
      const handleMouseMove = (event) => {
        mouse.x = event.x
        mouse.y = event.y
      }
      window.addEventListener('mousemove', handleMouseMove)

      class Particle {
        constructor(x, y, directionX, directionY, size, color) {
          this.x = x
          this.y = y
          this.directionX = directionX
          this.directionY = directionY
          this.size = size
          this.color = color
        }
        draw() {
          ctx.beginPath()
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2, false)
          ctx.fillStyle = 'rgba(100, 255, 218, 0.4)'
          ctx.fill()
        }
        update() {
          if (this.x > canvas.width || this.x < 0) {
            this.directionX = -this.directionX
          }
          if (this.y > canvas.height || this.y < 0) {
            this.directionY = -this.directionY
          }
          
          let dx = mouse.x - this.x
          let dy = mouse.y - this.y
          let distance = Math.sqrt(dx * dx + dy * dy)
          if (distance < mouse.radius + this.size) {
            if (mouse.x < this.x && this.x < canvas.width - this.size * 10) {
              this.x += 1
            }
            if (mouse.x > this.x && this.x > this.size * 10) {
              this.x -= 1
            }
            if (mouse.y < this.y && this.y < canvas.height - this.size * 10) {
              this.y += 1
            }
            if (mouse.y > this.y && this.y > this.size * 10) {
              this.y -= 1
            }
          }
          this.x += this.directionX * 0.3
          this.y += this.directionY * 0.3
          this.draw()
        }
      }

      const init = () => {
        particlesArray = []
        for (let i = 0; i < numberOfParticles; i++) {
          let size = (Math.random() * 2) + 1
          let x = (Math.random() * ((innerWidth - size * 2) - (size * 2)) + size * 2)
          let y = (Math.random() * ((innerHeight - size * 2) - (size * 2)) + size * 2)
          let directionX = (Math.random() * 2) - 1
          let directionY = (Math.random() * 2) - 1
          particlesArray.push(new Particle(x, y, directionX, directionY, size, 'white'))
        }
      }

      const connect = () => {
        for (let a = 0; a < particlesArray.length; a++) {
          for (let b = a; b < particlesArray.length; b++) {
            let distance = ((particlesArray[a].x - particlesArray[b].x) * (particlesArray[a].x - particlesArray[b].x)) + ((particlesArray[a].y - particlesArray[b].y) * (particlesArray[a].y - particlesArray[b].y))
            if (distance < (canvas.width / 7) * (canvas.height / 7)) {
              let opacityValue = 1 - (distance / 20000)
              ctx.strokeStyle = 'rgba(100, 255, 218,' + opacityValue * 0.15 + ')'
              ctx.lineWidth = 1
              ctx.beginPath()
              ctx.moveTo(particlesArray[a].x, particlesArray[a].y)
              ctx.lineTo(particlesArray[b].x, particlesArray[b].y)
              ctx.stroke()
            }
          }
        }
      }

      let animationId
      const animate = () => {
        animationId = requestAnimationFrame(animate)
        ctx.clearRect(0, 0, innerWidth, innerHeight)
        for (let i = 0; i < particlesArray.length; i++) {
          particlesArray[i].update()
        }
        connect()
      }

      const handleResize = () => {
        canvas.width = innerWidth
        canvas.height = innerHeight
        mouse.radius = ((canvas.height / 80) * (canvas.width / 80))
        init()
      }

      window.addEventListener('resize', handleResize)
      init()
      animate()

      // 保存清理函数和动画ID
      this._canvasAnimationId = animationId
      this._canvasCleanup = () => {
        window.removeEventListener('resize', handleResize)
        window.removeEventListener('mousemove', handleMouseMove)
        if (this._canvasAnimationId) {
          cancelAnimationFrame(this._canvasAnimationId)
          this._canvasAnimationId = null
        }
      }
    }
  },
  beforeUnmount() {
    // 清理 canvas 相关事件监听器
    if (this._canvasCleanup) {
      this._canvasCleanup()
      this._canvasCleanup = null
      this._canvasAnimationId = null
    }
  },
  activated() {
    // 如果使用了 keep-alive，组件激活时重新初始化 Canvas
    this.$nextTick(() => {
      setTimeout(() => {
        this.initCanvas()
      }, 100)
    })
  },
  deactivated() {
    // 如果使用了 keep-alive，组件停用时清理 Canvas
    if (this._canvasCleanup) {
      this._canvasCleanup()
      this._canvasCleanup = null
      this._canvasAnimationId = null
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700;900&display=swap');

:root {
  --bg-deep: #020c1b;
  --bg-tech: #0a192f;
  --card-bg: rgba(16, 32, 60, 0.75);
  --text-bright: #ffffff;
  --text-muted: #8892b0;
  --accent-cyan: #64ffda;
  --accent-blue: #007bff;
  --input-bg: rgba(2, 12, 27, 0.5);
}

.login-container {
  font-family: 'Inter', sans-serif;
  background-color: var(--bg-deep);
  height: 100vh;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--text-bright);
  position: relative;
}

/* 移动端允许滚动 */
@media (max-width: 900px) {
  .login-container {
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }
}

#canvas-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  background: radial-gradient(ellipse at center, var(--bg-tech) 0%, var(--bg-deep) 100%);
}

.split-container {
  display: flex;
  width: 90%;
  max-width: 1400px;
  align-items: center;
  justify-content: space-between;
  z-index: 10;
  transform: skewY(-2deg);
}

.left-intro-area {
  flex: 1;
  padding-right: 60px;
  transform: skewY(2deg);
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 900;
  line-height: 1.2;
  margin-bottom: 20px;
  text-shadow: 0 5px 20px rgba(0, 0, 0, 0.5);
  color: var(--text-bright);
}

.hero-title .highlight {
  color: var(--accent-cyan);
  position: relative;
  display: inline-block;
}

.hero-title .highlight::after {
  content: '';
  position: absolute;
  bottom: 5px;
  left: 0;
  width: 100%;
  height: 8px;
  background: rgba(100, 255, 218, 0.2);
  z-index: -1;
  transform: skewX(-10deg);
}

.hero-desc {
  font-size: 1.2rem;
  color: var(--text-muted);
  line-height: 1.6;
  max-width: 480px;
}

.right-login-area {
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  transform: skewY(2deg);
}

.login-card {
  background: var(--card-bg);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  padding: 40px 40px 0 40px;
  border-radius: 20px;
  width: 420px;
  max-width: 420px;
  flex-shrink: 0;
  border: 1px solid rgba(100, 255, 218, 0.1);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4), 0 0 30px rgba(100, 255, 218, 0.1);
  transition: all 0.4s ease;
  overflow: visible;
  position: relative;
}

.login-card:hover {
  transform: translateY(-10px) scale(1.02);
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.5), 0 0 50px rgba(100, 255, 218, 0.3);
  border-color: rgba(100, 255, 218, 0.3);
}

.card-header {
  text-align: center;
  margin-bottom: 30px;
}

.card-header h2 {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 10px;
  color: var(--text-bright);
}

.card-header p {
  color: var(--text-muted);
  font-size: 1rem;
}

.login-tabs {
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.1);
}

.tab-item {
  padding: 10px 15px;
  color: var(--text-muted);
  cursor: pointer;
  position: relative;
  font-weight: 500;
  transition: color 0.3s;
}

.tab-item.active {
  color: var(--accent-cyan);
}

.tab-item.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 2px;
  background: var(--accent-cyan);
  box-shadow: 0 -2px 10px var(--accent-cyan);
}

.form-container {
  margin-bottom: 24px;
}

.form-group {
  margin-bottom: 16px;
}

.custom-input {
  width: 100%;
  padding: 15px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  background: var(--input-bg);
  color: var(--text-bright);
  border-radius: 8px;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s;
  box-sizing: border-box;
}

.custom-input:focus {
  border-color: var(--accent-cyan);
  box-shadow: 0 0 15px rgba(100, 255, 218, 0.2);
  background: rgba(2, 12, 27, 0.8);
}

.custom-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.custom-input.error-input {
  border-color: #f44336;
}

.error-message {
  color: #f44336;
  font-size: 12px;
  margin-top: 4px;
  line-height: 1.4;
}

.captcha-group,
.code-group {
  display: flex;
  gap: 12px;
}

.captcha-input,
.code-input {
  flex: 1;
}

.captcha-img-box {
  flex-shrink: 0;
  width: 120px;
  height: 50px;
  background: var(--input-bg);
  border: 2px solid rgba(255, 255, 255, 0.1);
  color: var(--text-bright);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  letter-spacing: 2px;
  font-family: monospace;
  transition: all 0.3s;
  box-sizing: border-box;
}

.captcha-img-box:hover {
  border-color: var(--accent-cyan);
  box-shadow: 0 0 15px rgba(100, 255, 218, 0.2);
  background: rgba(2, 12, 27, 0.8);
}

.btn-code {
  padding: 12px 20px;
  background: rgba(100, 255, 218, 0.1);
  border: 2px solid var(--accent-cyan);
  border-radius: 8px;
  color: var(--accent-cyan);
  cursor: pointer;
  white-space: nowrap;
  font-size: 14px;
  transition: all 0.3s;
}

.btn-code:hover:not(:disabled) {
  background: var(--accent-cyan);
  color: var(--bg-deep);
}

.btn-code:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.login-btn {
  width: 100%;
  padding: 15px;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--bg-deep);
  background: linear-gradient(135deg, var(--accent-cyan), var(--accent-blue));
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 5px 15px rgba(0, 123, 255, 0.3);
  animation: btnPulse 3s infinite ease-in-out;
}

.login-btn:hover {
  transform: scale(1.02);
  box-shadow: 0 0 30px rgba(100, 255, 218, 0.8);
  animation-play-state: paused;
}

@keyframes btnPulse {
  0%, 100% {
    box-shadow: 0 5px 15px rgba(100, 255, 218, 0.3);
  }
  50% {
    box-shadow: 0 5px 25px rgba(100, 255, 218, 0.6);
  }
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  margin-left: -40px;
  margin-right: -40px;
  margin-bottom: 0;
  padding: 20px 40px 40px 40px;
  font-size: 0.9rem;
  width: calc(100% + 80px);
  box-sizing: border-box;
  position: relative;
}

.footer-link {
  color: var(--text-muted);
  cursor: pointer;
  text-decoration: none;
  transition: color 0.3s;
  display: block;
  white-space: nowrap;
}

.footer-link-left {
  margin-left: 0;
  padding-left: 0;
}

.footer-link-right {
  margin-right: 0;
  padding-right: 0;
  margin-left: auto;
}

.footer-link:hover {
  color: var(--accent-cyan);
}

.link-highlight {
  color: var(--accent-cyan);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--card-bg);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 20px;
  width: 90%;
  max-width: 420px;
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4), 0 0 30px rgba(100, 255, 218, 0.1);
  border: 1px solid rgba(100, 255, 218, 0.1);
  display: flex;
  flex-direction: column;
  /* 隐藏滚动条但保持滚动功能 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.modal-content::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-bright);
}

.close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  font-size: 24px;
  color: var(--text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: rgba(100, 255, 218, 0.1);
  color: var(--accent-cyan);
}

.modal-tabs {
  display: flex;
  justify-content: center;
  margin: 0 20px;
  padding-top: 20px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.1);
}

.modal-tabs .tab-item {
  padding: 10px 15px;
  color: var(--text-muted);
  cursor: pointer;
  position: relative;
  font-weight: 500;
  transition: color 0.3s;
}

.modal-tabs .tab-item.active {
  color: var(--accent-cyan);
}

.modal-tabs .tab-item.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 2px;
  background: var(--accent-cyan);
  box-shadow: 0 -2px 10px var(--accent-cyan);
}

.modal-form {
  padding: 20px;
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.modal-form .form-group {
  margin-bottom: 20px;
}

.modal-form .form-group input {
  width: 100%;
  padding: 15px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  background: var(--input-bg);
  color: var(--text-bright);
  border-radius: 8px;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s;
  box-sizing: border-box;
}

.modal-form .form-group input:focus {
  border-color: var(--accent-cyan);
  box-shadow: 0 0 15px rgba(100, 255, 218, 0.2);
  background: rgba(2, 12, 27, 0.8);
}

.modal-form .form-group input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

/* 移动端适配 - 平板和手机 */
@media (max-width: 900px) {
  .login-container {
    height: 100vh;
    height: 100dvh; /* 动态视口高度，适配移动端浏览器 */
    overflow-y: auto !important; /* 强制允许滚动 */
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    padding: 10px;
    padding-top: max(20px, env(safe-area-inset-top)); /* 适配手机状态栏，增加顶部间距 */
    padding-bottom: max(20px, env(safe-area-inset-bottom)); /* 适配底部安全区域 */
  }
  
  .split-container {
    flex-direction: column;
    transform: skewY(0deg);
    padding: 0;
    width: 100%;
    max-width: 100%;
    min-height: auto; /* 改为auto，让内容自然撑开 */
    justify-content: flex-start;
    align-items: center;
    padding-top: max(30px, calc(env(safe-area-inset-top) + 20px)); /* 增加顶部间距，避免被状态栏遮挡 */
    padding-bottom: 30px; /* 增加底部间距 */
  }
  
  .left-intro-area {
    transform: skewY(0deg);
    padding-right: 0;
    text-align: center;
    margin-bottom: 30px;
    width: 100%;
    padding: 0 15px;
    padding-top: 0; /* 移除这里的padding-top，由split-container统一控制 */
  }
  
  .right-login-area {
    transform: skewY(0deg);
    width: 100%;
    justify-content: center;
    flex: 1;
    display: flex;
    align-items: flex-start;
    padding: 0 15px;
    min-height: 0; /* 允许flex收缩 */
  }
  
  .login-card {
    width: 100%;
    max-width: 100%;
    padding: 30px 20px 0 20px;
    border-radius: 16px;
    margin-bottom: max(20px, env(safe-area-inset-bottom)); /* 底部间距适配安全区域 */
    flex-shrink: 0; /* 防止卡片被压缩 */
  }
  
  .login-card:hover {
    transform: none;
  }
  
  .hero-title {
    font-size: 2rem;
    line-height: 1.3;
    margin-bottom: 15px;
  }
  
  .hero-desc {
    font-size: 1rem;
    line-height: 1.5;
    max-width: 100%;
  }
  
  .card-header h2 {
    font-size: 1.5rem;
  }
  
  .card-header p {
    font-size: 0.9rem;
  }
  
  .login-tabs {
    margin-bottom: 25px;
    gap: 8px;
  }
  
  .tab-item {
    font-size: 0.85rem;
    padding: 10px 12px;
    flex: 1;
  }
  
  .form-group {
    margin-bottom: 18px;
  }
  
  .custom-input {
    font-size: 16px; /* 防止iOS自动缩放 */
    padding: 14px 16px;
    height: auto;
    min-height: 48px; /* 增加触摸目标大小 */
  }
  
  .captcha-group,
  .code-group {
    flex-direction: column;
    gap: 12px;
  }
  
  .captcha-input,
  .code-input {
    width: 100%;
    margin-right: 0;
  }
  
  .captcha-img-box {
    width: 100%;
    height: 48px;
    font-size: 1.2rem;
  }
  
  .btn-code {
    width: 100%;
    padding: 14px;
    font-size: 0.9rem;
    min-height: 48px;
  }
  
  .login-btn {
    width: 100%;
    padding: 16px;
    font-size: 1rem;
    min-height: 50px;
    margin-top: 10px;
  }
  
  .card-footer {
    flex-direction: column;
    gap: 15px;
    padding: 20px 0;
    text-align: center;
    margin-left: -20px;
    margin-right: -20px;
    width: calc(100% + 40px);
  }
  
  .footer-link {
    font-size: 0.85rem;
    padding: 10px 12px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    touch-action: manipulation;
    -webkit-tap-highlight-color: rgba(100, 255, 218, 0.2);
  }
  
  .footer-link-left,
  .footer-link-right {
    margin-left: 0;
    margin-right: 0;
  }
  
  /* 忘记密码弹窗移动端优化 */
  .modal-overlay {
    padding: 16px;
    align-items: flex-end;
  }
  
  .modal-content {
    width: 100%;
    max-width: 100%;
    max-height: 90vh;
    max-height: 90dvh;
    border-radius: 20px 20px 0 0;
    margin: 0;
  }
  
  .modal-header {
    padding: 16px 20px;
  }
  
  .modal-header h3 {
    font-size: 18px;
  }
  
  .modal-tabs {
    margin: 0 20px;
    padding-top: 16px;
  }
  
  .modal-tabs .tab-item {
    padding: 10px 12px;
    font-size: 0.85rem;
  }
  
  .modal-form {
    padding: 16px 20px;
    max-height: calc(90vh - 180px);
    max-height: calc(90dvh - 180px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  .modal-form .form-group {
    margin-bottom: 16px;
  }
  
  .modal-form .form-group input {
    font-size: 16px;
    padding: 14px;
    min-height: 48px;
  }
  
  .modal-form .code-group {
    flex-direction: column;
    gap: 12px;
  }
  
  .modal-form .code-input {
    width: 100%;
    margin-right: 0;
  }
  
  .modal-form .btn-code {
    width: 100%;
    padding: 14px;
    font-size: 0.9rem;
    min-height: 48px;
  }
  
  .modal-form .login-btn {
    width: 100%;
    padding: 16px;
    font-size: 1rem;
    min-height: 50px;
    margin-top: 10px;
  }
  
  .form-container {
    min-height: 240px;
  }
}

/* 小屏手机适配 */
@media (max-width: 480px) {
  .login-container {
    padding: 5px;
    padding-top: max(15px, env(safe-area-inset-top)); /* 适配手机状态栏，增加顶部间距 */
    padding-bottom: max(15px, env(safe-area-inset-bottom)); /* 适配底部安全区域 */
  }
  
  .split-container {
    padding-top: max(25px, calc(env(safe-area-inset-top) + 15px)); /* 小屏也增加顶部间距 */
    padding-bottom: 25px; /* 增加底部间距 */
  }
  
  .left-intro-area {
    margin-bottom: 20px;
    padding: 0 10px;
    padding-top: 0; /* 移除这里的padding-top，由split-container统一控制 */
  }
  
  .hero-title {
    font-size: 1.75rem;
    margin-bottom: 12px;
  }
  
  .hero-desc {
    font-size: 0.9rem;
    line-height: 1.4;
  }
  
  .right-login-area {
    padding: 0 10px;
  }
  
  .login-card {
    padding: 25px 15px 0 15px;
    border-radius: 12px;
  }
  
  .card-header h2 {
    font-size: 1.3rem;
    margin-bottom: 8px;
  }
  
  .card-header p {
    font-size: 0.85rem;
  }
  
  .login-tabs {
    margin-bottom: 20px;
    gap: 6px;
  }
  
  .tab-item {
    font-size: 0.8rem;
    padding: 8px 10px;
  }
  
  .form-group {
    margin-bottom: 15px;
  }
  
  .custom-input {
    padding: 12px 14px;
    font-size: 16px;
    min-height: 44px;
  }
  
  .login-btn {
    padding: 14px;
    font-size: 0.95rem;
    min-height: 48px;
  }
  
  .card-footer {
    padding: 15px 0;
    gap: 12px;
    margin-left: -15px;
    margin-right: -15px;
    width: calc(100% + 30px);
  }
  
  .footer-link {
    font-size: 0.8rem;
    padding: 10px 12px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    touch-action: manipulation;
    -webkit-tap-highlight-color: rgba(100, 255, 218, 0.2);
  }
  
  /* 忘记密码弹窗小屏优化 */
  .modal-overlay {
    padding: 10px;
  }
  
  .modal-content {
    max-height: 95vh;
    max-height: 95dvh;
  }
  
  .modal-form {
    padding: 12px 15px;
    max-height: calc(95vh - 160px);
    max-height: calc(95dvh - 160px);
  }
  
  .modal-form .form-group input {
    padding: 12px;
    min-height: 44px;
  }
  
  .modal-form .btn-code {
    padding: 12px;
    min-height: 44px;
  }
  
  .modal-form .login-btn {
    padding: 14px;
    min-height: 48px;
  }
}

/* 横屏适配 */
@media (max-width: 900px) and (orientation: landscape) {
  .login-container {
    padding: 10px;
  }
  
  .split-container {
    flex-direction: row;
    align-items: center;
    gap: 20px;
  }
  
  .left-intro-area {
    flex: 0 0 40%;
    margin-bottom: 0;
    padding-right: 20px;
  }
  
  .hero-title {
    font-size: 1.8rem;
  }
  
  .hero-desc {
    font-size: 0.9rem;
  }
  
  .right-login-area {
    flex: 1;
    padding: 0;
  }
  
  .login-card {
    max-width: 400px;
    padding: 25px 25px 0 25px;
  }
}

/* ==================== 表单切换动画 ==================== */
/* 表单淡入淡出和滑动效果 */
.form-fade-enter-active,
.form-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.form-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px) scale(0.98);
}

.form-fade-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.form-fade-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.form-fade-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.98);
}

/* 表单容器优化，确保动画流畅 */
.form-container {
  position: relative;
  min-height: 280px; /* 设置最小高度，避免切换时高度跳动 */
  overflow: hidden;
}

.form {
  width: 100%;
}

/* 弹窗淡入淡出动画 */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-fade-enter-active .modal-content,
.modal-fade-leave-active .modal-content {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-fade-enter-from {
  opacity: 0;
}

.modal-fade-enter-from .modal-content {
  opacity: 0;
  transform: translateY(-20px) scale(0.96);
}

.modal-fade-enter-to {
  opacity: 1;
}

.modal-fade-enter-to .modal-content {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.modal-fade-leave-from {
  opacity: 1;
}

.modal-fade-leave-from .modal-content {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-leave-to .modal-content {
  opacity: 0;
  transform: translateY(-15px) scale(0.95);
}

/* 密码强度指示器 */
.password-strength {
  display: flex;
  align-items: center;
  margin-top: 6px;
  padding-left: 2px;
}

.strength-label {
  font-size: 13px;
  color: var(--text-muted);
  margin-right: 8px;
}

.strength-bars {
  flex: 1;
  max-width: 100px;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

.strength-bar {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease, background-color 0.3s ease;
}

.strength-1 {
  width: 33%;
  background-color: #f44336;
}

.strength-2 {
  width: 66%;
  background-color: #ff9800;
}

.strength-3 {
  width: 100%;
  background-color: #4caf50;
}

.strength-text {
  margin-left: 8px;
  font-size: 12px;
  color: var(--accent-cyan);
}
</style>
